import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, inr, Money } from "@/lib/api";
import { CrudPage } from "@/components/CrudPage";
import { DataTable, DemoBadge, PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AGENT_FIELDS = [
  { key: "agency_name", label: "Agency Name", required: true },
  { key: "contact_person", label: "Contact Person" },
  { key: "gstin", label: "GSTIN" },
  { key: "pan", label: "PAN" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "address", label: "Address", full: true },
  { key: "state", label: "State", type: "state" },
  { key: "credit_limit", label: "Credit Limit (₹)", type: "number" },
  { key: "payment_terms", label: "Payment Terms", placeholder: "e.g. Net 15" },
  { key: "commission_pct", label: "Commission %", type: "number" },
  { key: "status", label: "Status", type: "select",
    options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
  { key: "notes", label: "Notes", type: "textarea", full: true },
];

export function Agents() {
  const navigate = useNavigate();
  return (
    <CrudPage title="B2B Travel Agents" subtitle="Agent network with credit limits, commissions & ledgers"
      testid="agents" endpoint="/agents" searchPlaceholder="Search agency, GSTIN…"
      createLabel="New Agent" fields={AGENT_FIELDS}
      defaults={{ agency_name: "", contact_person: "", gstin: "", pan: "", phone: "", email: "",
        address: "", state: "", state_code: "", credit_limit: 0, payment_terms: "",
        commission_pct: 0, status: "active", notes: "" }}
      columns={[
        { key: "agent_id", label: "ID" },
        { key: "agency_name", label: "Agency", render: (r) => (
          <div><span className="font-medium">{r.agency_name}</span>{r.is_demo && <DemoBadge />}
            <div className="text-xs text-[#5C6B69]">{r.contact_person}</div></div>) },
        { key: "gstin", label: "GSTIN", render: (r) => r.gstin || "—" },
        { key: "state", label: "State" },
        { key: "credit_limit", label: "Credit Limit", align: "right", render: (r) => <Money value={r.credit_limit} /> },
        { key: "commission_pct", label: "Comm %", align: "right", render: (r) => `${r.commission_pct}%` },
        { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
      ]}
      onRow={(r) => navigate(`/agents/${r.id}`)} />
  );
}

export function AgentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/agents/${id}/ledger`).then((r) => setData(r.data)).catch((e) => {
      toast.error(errMsg(e));
      navigate("/agents");
    });
  }, [id, navigate]);
  useEffect(load, [load]);
  if (!data) return <div className="py-24 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-[#5C6B69]" /></div>;
  const { agent: a, totals, invoices, payments, bookings } = data;
  const overLimit = a.credit_limit > 0 && totals.outstanding > a.credit_limit;
  return (
    <div data-testid="agent-detail-page">
      <PageHeader title={<span className="flex items-center gap-2">{a.agency_name} {a.is_demo && <DemoBadge />}</span>}
        subtitle={`${a.agent_id} · ${a.contact_person} · ${a.state || ""} · Commission ${a.commission_pct}% · ${a.payment_terms || "No terms"}`}
        testid="agent-detail-header"
        actions={<Button variant="outline" onClick={() => navigate("/agents")} data-testid="agent-detail-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>} />
      {overLimit && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-4 text-sm font-medium" data-testid="agent-credit-warning">
          ⚠ Outstanding {inr(totals.outstanding)} exceeds credit limit {inr(a.credit_limit)}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[["Total Sales", totals.sales], ["Collected", totals.collected], ["Credit Notes", totals.credit_notes],
          ["Outstanding", totals.outstanding], ["Commission Earned", totals.commission]].map(([l, v], i) => (
          <div key={l} className="bg-white border border-[#E5E0D8] rounded-xl p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-[#5C6B69]">{l}</div>
            <div className={`num text-xl font-bold mt-1 ${i === 3 ? "text-red-600" : "text-[#0D4F4A]"}`}>{inr(v)}</div>
          </div>
        ))}
      </div>
      <Tabs defaultValue="invoices">
        <TabsList>
          <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="payments">Collections ({payments.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-4">
          <DataTable testid="agent-invoices" columns={[
            { key: "invoice_no", label: "Invoice", render: (r) => <span className="font-semibold text-[#0D4F4A]">{r.invoice_no}</span> },
            { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
            { key: "customer", label: "End Customer", render: (r) => r.customer?.name },
            { key: "grand_total", label: "Total", align: "right", render: (r) => <Money value={r.grand_total} /> },
            { key: "balance", label: "Balance", align: "right", render: (r) => <Money value={r.balance} /> },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          ]} rows={invoices} onRow={(r) => navigate(`/invoices/${r.id}`)} empty="No invoices" />
        </TabsContent>
        <TabsContent value="bookings" className="mt-4">
          <DataTable testid="agent-bookings" columns={[
            { key: "booking_id", label: "ID" }, { key: "customer_name", label: "Customer" },
            { key: "destination", label: "Destination" },
            { key: "start_date", label: "Travel", render: (r) => fmtDate(r.start_date) },
            { key: "total", label: "Total", align: "right", render: (r) => <Money value={r.total} /> },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          ]} rows={bookings} empty="No bookings" />
        </TabsContent>
        <TabsContent value="payments" className="mt-4">
          <DataTable testid="agent-payments" columns={[
            { key: "receipt_no", label: "Receipt" }, { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
            { key: "customer_name", label: "Customer" }, { key: "mode", label: "Mode" },
            { key: "amount", label: "Amount", align: "right", render: (r) => <Money value={r.amount} /> },
          ]} rows={payments} empty="No collections" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
